// components/ExpenseFilters.js
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Collapse from '@mui/material/Collapse';
import Paper from '@mui/material/Paper';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import FilterListIcon from '@mui/icons-material/FilterList';
import TuneIcon from '@mui/icons-material/Tune';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CloseIcon from '@mui/icons-material/Close';

const FilterContainer = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2),
}));

const SearchBox = styled(TextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: '8px',
        backgroundColor: theme.palette.background.paper,
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main,
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.divider,
        },
    },
    width: '100%',
}));

const DateRangeContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(2),
    width: '100%',
    [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
    },
}));

const FilterHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
}));

const FiltersTitle = styled(Typography)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    fontWeight: 500,
}));

const FilterButton = styled(Button)(({ theme }) => ({
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    '&:hover': {
        backgroundColor: theme.palette.primary.dark,
    },
    borderRadius: '8px',
    textTransform: 'none',
    fontWeight: 500,
}));

const MobileFilterTrigger = styled(Button)(({ theme, expanded }) => ({
    width: '100%',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5),
    marginBottom: expanded ? theme.spacing(2) : 0,
    borderRadius: '8px',
    textTransform: 'none',
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.primary,
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
    },
}));

const ExpenseFilters = ({
                            dateFrom,
                            dateTo,
                            searchTerm,
                            onDateChange,
                            onSearch,
                            onAddExpense,
                            isMobile
                        }) => {
    const [expanded, setExpanded] = useState(false);
    const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);

    const handleSearchChange = (event) => {
        setLocalSearchTerm(event.target.value);
    };

    const handleSearchSubmit = () => {
        onSearch(localSearchTerm);
    };

    const handleClearSearch = () => {
        setLocalSearchTerm('');
        onSearch('');
    };

    const handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            handleSearchSubmit();
        }
    };

    const handleDateFromChange = (newValue) => {
        onDateChange(newValue, dateTo);
    };

    const handleDateToChange = (newValue) => {
        onDateChange(dateFrom, newValue);
    };

    const toggleExpanded = () => {
        setExpanded(!expanded);
    };

    return (
        <FilterContainer>
            {isMobile ? (
                <>
                    <MobileFilterTrigger
                        expanded={expanded}
                        onClick={toggleExpanded}
                        startIcon={<TuneIcon />}
                        endIcon={expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    >
                        Filters & Search
                    </MobileFilterTrigger>

                    <Collapse in={expanded}>
                        <Box sx={{ mb: 2 }}>
                            <SearchBox
                                fullWidth
                                placeholder="Search expenses..."
                                value={localSearchTerm}
                                onChange={handleSearchChange}
                                onKeyPress={handleKeyPress}
                                variant="outlined"
                                size="small"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon color="action" />
                                        </InputAdornment>
                                    ),
                                    endAdornment: localSearchTerm && (
                                        <InputAdornment position="end">
                                            <IconButton
                                                size="small"
                                                onClick={handleClearSearch}
                                                edge="end"
                                            >
                                                <CloseIcon fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>

                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CalendarMonthIcon fontSize="small" color="primary" />
                                Date Range
                            </Typography>
                            <DateRangeContainer>
                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                    <DatePicker
                                        label="From Date"
                                        value={dateFrom}
                                        onChange={handleDateFromChange}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                fullWidth
                                                size="small"
                                                variant="outlined"
                                            />
                                        )}
                                    />

                                    <DatePicker
                                        label="To Date"
                                        value={dateTo}
                                        onChange={handleDateToChange}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                fullWidth
                                                size="small"
                                                variant="outlined"
                                            />
                                        )}
                                    />
                                </LocalizationProvider>
                            </DateRangeContainer>
                        </Box>

                        <Button
                            variant="contained"
                            color="success"
                            fullWidth
                            startIcon={<AddIcon />}
                            onClick={onAddExpense}
                        >
                            Add New Expense
                        </Button>
                    </Collapse>
                </>
            ) : (
                <>
                    <FilterHeader>
                        <FiltersTitle variant="h6">
                            <FilterListIcon color="primary" />
                            Filter Expenses
                        </FiltersTitle>

                        <Button
                            startIcon={<SearchIcon />}
                            variant="contained"
                            disableElevation
                            onClick={handleSearchSubmit}
                            sx={{ borderRadius: '8px' }}
                        >
                            Search
                        </Button>
                    </FilterHeader>

                    <Grid container spacing={3}>
                        <Grid item xs={12} md={4}>
                            <Box>
                                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                                    Search by Description
                                </Typography>
                                <SearchBox
                                    fullWidth
                                    placeholder="Enter keywords..."
                                    value={localSearchTerm}
                                    onChange={handleSearchChange}
                                    onKeyPress={handleKeyPress}
                                    variant="outlined"
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon color="action" />
                                            </InputAdornment>
                                        ),
                                        endAdornment: localSearchTerm && (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    size="small"
                                                    onClick={handleClearSearch}
                                                    edge="end"
                                                >
                                                    <CloseIcon fontSize="small" />
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Box>
                        </Grid>

                        <Grid item xs={12} md={8}>
                            <Box>
                                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CalendarMonthIcon fontSize="small" color="primary" />
                                    Date Range
                                </Typography>
                                <DateRangeContainer>
                                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                                        <DatePicker
                                            label="From Date"
                                            value={dateFrom}
                                            onChange={handleDateFromChange}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    fullWidth
                                                    variant="outlined"
                                                    InputLabelProps={{ shrink: true }}
                                                />
                                            )}
                                        />

                                        <DatePicker
                                            label="To Date"
                                            value={dateTo}
                                            onChange={handleDateToChange}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    fullWidth
                                                    variant="outlined"
                                                    InputLabelProps={{ shrink: true }}
                                                />
                                            )}
                                        />
                                    </LocalizationProvider>
                                </DateRangeContainer>
                            </Box>
                        </Grid>
                    </Grid>
                </>
            )}
        </FilterContainer>
    );
};

ExpenseFilters.propTypes = {
    dateFrom: PropTypes.object.isRequired,
    dateTo: PropTypes.object.isRequired,
    searchTerm: PropTypes.string.isRequired,
    onDateChange: PropTypes.func.isRequired,
    onSearch: PropTypes.func.isRequired,
    onAddExpense: PropTypes.func.isRequired,
    isMobile: PropTypes.bool.isRequired,
};

export default ExpenseFilters;